import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import Slider from 'react-slick'
import styles from './BottomSlider.module.scss'
import styled from 'styled-components'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { EventPost, fetchActiveEvents } from 'lib/fetchActiveEvents'

const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  const daysOfWeek = ['日', '月', '火', '水', '木', '金', '土']
  const dayOfWeek = daysOfWeek[date.getDay()]
  return {
    fullDate: `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${dayOfWeek}）`,
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    dayOfWeek: dayOfWeek,
  }
}

const getEventDateDisplay = (post) => {
  const startInfo = post.start_date ? formatDate(post.start_date) : null
  const endInfo = post.end_date ? formatDate(post.end_date) : null

  if (startInfo && endInfo) {
    if (startInfo.fullDate === endInfo.fullDate) {
      return startInfo.fullDate
    } else if (startInfo.year === endInfo.year) {
      if (startInfo.month === endInfo.month) {
        return `${startInfo.year}年${startInfo.month}月${startInfo.day}日（${startInfo.dayOfWeek}）〜${endInfo.day}日（${endInfo.dayOfWeek}）`
      } else {
        return `${startInfo.year}年${startInfo.month}月${startInfo.day}日（${startInfo.dayOfWeek}）〜${endInfo.month}月${endInfo.day}日（${endInfo.dayOfWeek}）`
      }
    } else {
      return `${startInfo.fullDate}〜${endInfo.fullDate}`
    }
  } else {
    return startInfo?.fullDate || endInfo?.fullDate || ''
  }
}

const BottomSlider: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false)
  const [posts, setPosts] = useState<EventPost[] | null>(null)

  useEffect(() => {
    setIsMobile(window.innerWidth < 820)
    const handleResize = () => {
      setIsMobile(window.innerWidth < 820)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    fetchActiveEvents().then(setPosts)
  }, [])

  if (posts === null) {
    return null
  }

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    variableWidth: true,
    autoplay: false,
    arrows: false,
    responsive: [
      {
        breakpoint: 820,
        settings: {
          slidesToShow: 1,
          variableWidth: false,
          centerMode: true,
          centerPadding: '12%',
        },
      },
    ],
  }

  const renderSlider = () => {
    const list = []
    for (let i = 0; i < posts.length; i++) {
      list.push(
        <Link href={`/events/${posts[i].id}`} key={posts[i].id}>
          <div className={styles.eventlist_item}>
            <figure className={styles.eventlist_item_img}>
              <img
                src={posts[i].thumbnail_img || '/images/img/img_thumbnail_dummy.png'}
                alt={posts[i].title.rendered}
              />
            </figure>
            <p className={styles.eventlist_item_date}>{getEventDateDisplay(posts[i])}</p>
            <h3 className={styles.eventlist_item_title}>{posts[i].title.rendered}</h3>
          </div>
        </Link>,
      )
    }
    return <Slider {...settings}>{list}</Slider>
  }

  const renderStaticEvents = () => {
    const list = []
    for (let i = 0; i < posts.length; i++) {
      list.push(
        <Link href={`/events/${posts[i].id}`} key={posts[i].id}>
          <div className={`${styles.eventlist_item} ${styles.eventlist_staticitem}`}>
            <figure className={styles.eventlist_item_img}>
              <img
                src={posts[i].thumbnail_img || '/images/img/img_thumbnail_dummy.png'}
                alt={posts[i].title.rendered}
              />
            </figure>
            <p className={styles.eventlist_item_date}>{getEventDateDisplay(posts[i])}</p>
            <h3 className={styles.eventlist_item_title}>{posts[i].title.rendered}</h3>
          </div>
        </Link>,
      )
    }
    return <div className={styles.eventsContainer}>{list}</div>
  }

  return posts.length === 0 ? (
    <div className={styles.slider}>
      <h2 className={styles.eventlist}>開催予定のイベント・セミナー</h2>
      <p className={styles.noEvents}>開催予定のイベント・セミナーはありません。</p>
    </div>
  ) : (
    <SliderWrapper className={styles.slider}>
      <h2 className={styles.eventlist}>開催予定のイベント・セミナー</h2>
      {isMobile || posts.length > 4 ? renderSlider() : renderStaticEvents()}
    </SliderWrapper>
  )
}

const SliderWrapper = styled.div`
  .slick-slide {
    padding: 0 8px;
    @media (min-width: 820px) {
      max-width: 280px;
      width: 19.44vw;
      margin-right: 20px;
      padding: 0;
    }
  }

  .slick-dots {
    margin: 20px 0 0;
    position: relative;
    width: auto;
    bottom: 0;
    text-align: left;
    @media (min-width: 720px) and (max-width: 1199px) {
      margin-left: 40px;
    }
    @media (max-width: 819px) {
      margin-left: 24px;
    }
    @media (min-width: 1200px) {
      margin-left: calc((100% - min(82%, 1180px)) / 2);
    }
  }

  .slick-dots li {
    position: relative;
    width: 40px;
    height: 10px;
    margin: 0 0 0 8px;
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 4px;
      top: 0;
      bottom: 0;
      margin: auto 0;
      background: #ccc;
    }
    @media (max-width: 819px) {
      width: 32px;
    }
  }

  .slick-dots li:first-child {
    margin: 0;
  }

  .slick-dots li.slick-active::after {
    background: #db0032;
  }

  .slick-dots li button {
    width: 100%;
    height: 100%;
    padding: 0;
    background: transparent;
  }

  .slick-dots li button::before {
    content: none;
  }
`

export default BottomSlider
